import { z } from "zod";
import { blankAsNull } from "./fields";
import { storableText } from "./appointments";
import { recordId } from "./contracts";
import { emailAddress } from "./organization-settings";

export const ASSIGNMENT_ROLE_MAX_LENGTH = 200;
export const ASSIGNMENT_NOTES_MAX_LENGTH = 2000;

export const propertyPersonDetails = z.object({
  person: recordId,
  email: z.string().nullable(),
  image: z.string().nullable(),
  role: z.string().nullable(),
  assignedAt: z.string(),
});
export type PropertyPersonDetails = z.infer<typeof propertyPersonDetails>;

export const propertyPerson = z.object({
  id: recordId,
  property: recordId,
  name: z.string(),
  phone: z.string().nullable(),
  onSiteContact: z.boolean(),
  details: propertyPersonDetails.optional(),
  notes: z.string().nullable().optional(),
});
export type PropertyPerson = z.infer<typeof propertyPerson>;

export const propertyPeople = z.object({ items: z.array(propertyPerson) });
export type PropertyPeople = z.infer<typeof propertyPeople>;

const statedText = (maxLength: number) => z.preprocess(blankAsNull, storableText(maxLength).nullable());

const roleText = statedText(ASSIGNMENT_ROLE_MAX_LENGTH);

const notesText = statedText(ASSIGNMENT_NOTES_MAX_LENGTH);

export const assignmentDetailsRequest = z
  .object({
    role: roleText,
    notes: notesText,
    onSiteContact: z.boolean(),
  })
  .strict();
export type AssignmentDetailsRequest = { role: string | null; notes: string | null; onSiteContact: boolean };

export const propertyAssignmentRequest = z
  .object({
    person: recordId.toLowerCase(),
    role: roleText.optional().transform((value) => value ?? null),
    notes: notesText.optional().transform((value) => value ?? null),
    onSiteContact: z.boolean().default(false),
  })
  .strict();
export type PropertyAssignmentRequest = { person: string; role?: string | null; notes?: string | null; onSiteContact?: boolean };

export const propertyContactRequest = z
  .object({
    email: emailAddress,
    role: roleText.optional().transform((value) => value ?? null),
    notes: notesText.optional().transform((value) => value ?? null),
    onSiteContact: z.boolean().default(false),
  })
  .strict();
export type PropertyContactRequest = { email: string; role?: string | null; notes?: string | null; onSiteContact?: boolean };

export const teamRole = z.enum(["customerAdmin", "propertyManager", "member"]);
export type TeamRole = z.infer<typeof teamRole>;

export const teamMember = z.object({
  id: recordId,
  name: z.string(),
  email: z.string(),
  role: teamRole,
});
export type TeamMember = z.infer<typeof teamMember>;

export const customerTeam = z.object({ items: z.array(teamMember) });
export type CustomerTeam = z.infer<typeof customerTeam>;
